import React, { useContext, useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import {
  Button,
  Dialog,
  EmptyState,
  Icon,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader
} from '@clientbase/clientbase-library'
import {
  Box,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material'
import { balanceDetails } from 'api/requests'
import { useAuthContext } from 'context/AuthContext'
import { BalanceDetailItem } from 'models/BalanceDetails'
import { ReceivablesParams } from 'models/Receivables'
import { ReceivablesService } from 'services/ReceivablesService'
import { ChargeContext, ChargeContextProvider } from 'templates/Charge/context'
import {
  NegativationContext,
  NegativationProvider
} from 'templates/v2/Negativation/components/context'
import NegativationDetail from 'templates/v2/Negativation/components/Detail'

import Can from 'components/Can'
import DialogReport from 'components/Dialog/DialogReport'
import { ModalTransfers } from 'components/ModalTransfers/ModalTransfers'
import { Table } from 'components/Table'
import Tabs from 'components/Tabs'

import { formatMoney } from 'utils'
import { useExportReport } from 'utils/filters/export-report'
import { ReceivablesTabs } from 'utils/tabs'

import ModalBalanceExpenseDetail from '../ModalBalanceExpenseDetail'
import ModalBalanceExpenseGroup from '../ModalBalanceExpenseGroup'
import ModalChargeDetail from '../ModalChargeDetail'
import ModalInvoiceDetails from '../ModalInvoiceDetails'
import ModalReport from '../ModalReport'
import { AntecipationsList } from './components/AntecipationsList/AntecipationsList'
import { BalanceTableSkeleton } from './components/BalanceTableSkeleton'
import CalendarReceivables from './components/CalendarReceivables/CalendarReceivables'
import { DisputesList } from './components/Disputes/DisputesList'
import { RequestAntecipation } from './components/RequestAntecipation/RequestAntecipation'
import {
  BALANCE_MODAL_HEIGHT,
  BALANCE_MODAL_MAX_WIDTH
} from './ModalBalance.constants'
import * as S from './ModalBalance.styles'
import { convertToRows } from './ModalBalance.utils'
export type Record = {
  receivable?: ReceivablesParams
}

export type ModalBalanceProps = {
  isOpen: boolean
  handleClose: () => void
  balance: number | false
  balanceScheduled: number | false
  initialTab?: string
  initialDisputeUuid?: string | null
  hasActiveDisputes?: boolean
}

const tablePropsInitialValues = {
  page: 0,
  rowsPerPage: 25,
  totalCount: 0
}

type BalanceDetails = Promise<{
  records: BalanceDetailItem[]
  automatic_transfer: boolean
}>

// SC-11258: cada aba diz de onde vem o número e para que serve. Sem isso o merchant
// confundia o "Saldo a liberar" (vendas já feitas, pela liberação) com as "Entradas
// previstas" do Fluxo de Caixa (tudo que deve cair no mês, inclusive o que vai vencer).
const TAB_DESCRIPTIONS: { [tab: string]: string | undefined } = {
  [ReceivablesTabs.RECEIVABLES]:
    'Valores já liberados e disponíveis para transferir, já sem as taxas.',
  [ReceivablesTabs.CALENDAR_RECEIVABLES]:
    'Vendas que seus clientes já pagaram e que ainda vão ser liberadas, na data de liberação e já sem as taxas. No cartão, a data depende do plano de cartão na hora da transação: no parcelado, as parcelas podem cair todas juntas ou uma por mês. Para a previsão completa do mês, incluindo cobranças que ainda vão vencer, use o Fluxo de Caixa.'
}

const buildTabs = (hasDisputes: boolean, activeTab: string) => [
  { label: 'Saldo', value: ReceivablesTabs.RECEIVABLES },
  {
    label: 'Saldo a liberar',
    value: ReceivablesTabs.CALENDAR_RECEIVABLES
  },
  {
    label: 'Antecipações',
    value: ReceivablesTabs.ANTECIPATIONS,
    // tooltip de regras só aparece quando a aba Antecipações está ativa
    adornment:
      activeTab === ReceivablesTabs.ANTECIPATIONS ? (
        <Tooltip
          enterTouchDelay={0}
          arrow
          placement="right"
          title={
            <span>
              <strong>Regras de antecipação:</strong>
              <br />• Antecipações pedidas após as 16h serão processadas no
              próximo dia útil
              <br />• Prazo de análise de até 72 horas úteis
              <br />• Só é possível antecipar recebíveis pagos há mais de 1 dia
              <br />• Após aprovada, a antecipação estará disponível para
              transferência em 1 dia útil
            </span>
          }
        >
          <Box display="flex">
            <Icon
              icon="help"
              sx={{
                fontSize: '1.2rem',
                color: 'neutral.700',
                cursor: 'pointer'
              }}
            />
          </Box>
        </Tooltip>
      ) : undefined
  },
  {
    label: 'Contestações',
    value: ReceivablesTabs.CONTESTS,
    dot: hasDisputes
  }
]

const NegativationDetailOpener = ({
  uuid,
  onDone
}: {
  uuid: string
  onDone: () => void
}) => {
  const { updateModalDetail } = useContext(NegativationContext)
  useEffect(() => {
    if (uuid) {
      updateModalDetail({ open: true, uuid })
      onDone()
    }
  }, [uuid, updateModalDetail, onDone])
  return null
}

const NfseDetailOpener = ({
  uuid,
  onDone
}: {
  uuid: string
  onDone: () => void
}) => {
  const { toggleModal } = useContext(ChargeContext)
  useEffect(() => {
    if (uuid) {
      toggleModal('detailInvoice', uuid)
      onDone()
    }
  }, [uuid, toggleModal, onDone])
  return null
}

const TransferDetailOpener = ({
  uuid,
  onDone
}: {
  uuid: string
  onDone: () => void
}) => {
  const { toggleModal } = useContext(ChargeContext)
  useEffect(() => {
    if (uuid) {
      toggleModal('isOpenDetails', uuid)
      onDone()
    }
  }, [uuid, toggleModal, onDone])
  return null
}

export const ModalBalance = ({
  isOpen,
  handleClose,
  balance,
  balanceScheduled,
  initialTab,
  initialDisputeUuid,
  hasActiveDisputes = false
}: ModalBalanceProps) => {
  const [tab, setTab] = useState<string>(
    initialTab ?? ReceivablesTabs.RECEIVABLES
  )
  const [loading, setLoading] = useState(false)

  const [openDialogReport, setOpenDialogReport] = useState<boolean>(false)
  const [dialogRedirectReport, setDialogRedirectReport] = useState(false)

  const [tableFetchLoading, setTableFetchLoading] = useState(false)
  const [records, setRecords] = useState<BalanceDetailItem[] | null>(null)
  const [tableProps, setTableProps] = useState(tablePropsInitialValues)
  const [negativationDetailUuid, setNegativationDetailUuid] = useState('')
  const [nfseDetailUuid, setNfseDetailUuid] = useState('')
  const [transferDetailUuid, setTransferDetailUuid] = useState('')

  const [modal, setModal] = useState({
    isOpenDetailInvoice: false,
    uuid: ''
  })

  // Linha agrupada do extrato (SC-10840): guarda o rótulo e os uuids do grupo.
  const [expenseGroupModal, setExpenseGroupModal] = useState<{
    isOpen: boolean
    title: string
    items: NonNullable<BalanceDetailItem['items']>
    dateLabel?: string
  }>({ isOpen: false, title: '', items: [] })
  const [expenseModal, setExpenseModal] = useState({
    isOpen: false,
    uuid: ''
  })

  const [antecipationView, setAntecipationView] = useState<
    'history' | 'request'
  >('history')
  const [selectedPaymentUuids, setSelectedPaymentUuids] = useState<string[]>([])

  const { capabilities } = useAuthContext()
  const allowAntecipation =
    capabilities?.credit_card_plans?.allow_antecipation ?? false

  const handleRequestAntecipationClick = () => {
    if (!allowAntecipation) {
      toast.error(
        'Antecipação não está ativo em sua conta. Para habilitar essa função entre em contato com nosso suporte via WhatsApp ou pelo Chat clicando no ícone do canto direito inferior da tela.',
        { autoClose: 8000 }
      )
      return
    }
    setAntecipationView('request')
  }

  const isMobile = useMediaQuery('(max-width: 600px)')

  const handleChangeTab = (_: React.SyntheticEvent, tab: string | number) => {
    setTab(String(tab))
    setAntecipationView('history')
    setSelectedPaymentUuids([])
  }

  const setIsOpenDialog = (isOpenDialog: boolean) => {
    setDialog((s) => ({ ...s, isOpenDialog }))
  }

  const handleCloseDialog = () => {
    setDialog((s) => ({
      ...s,
      isOpenDialog: false
    }))
  }

  const {
    handleExportReport,
    loading: exportLoading,
    handleRequestWithdrawal
  } = useExportReport({
    functionToCallAfterSubmitReport: () => {
      setOpenDialogReport(true)
    },
    functionToCallAfterSubmitTransfer: (message: string) => {
      setDialog((prevState) => ({
        ...prevState,
        description: '',
        isOpenDialog: false,
        title: ''
      }))
      if (!message.includes('Não foi possível solicitar a transferência')) {
        handleClose()
      }
    },
    model: 'Receivable',
    setIsOpenDialog
  })

  const {
    handleExportReport: handleExportPendingReport,
    loading: exportPendingLoading
  } = useExportReport({
    functionToCallAfterSubmitReport: () => {
      setOpenDialogReport(true)
    },
    model: 'Receivable',
    filters: { status: 'pending' },
    setIsOpenDialog
  })

  const [dialog, setDialog] = useState<{
    action: () => Promise<void> | null
    description: string
    handleClose: () => void | null
    isOpenDialog: boolean
    title: string
    actionLabel: string
  }>({
    action: handleExportReport,
    description: '',
    handleClose: handleCloseDialog,
    isOpenDialog: false,
    title: '',
    actionLabel: ''
  })

  const handleCloseModalBalance = () => {
    handleClose()
    setTableProps((s) => ({ ...s, page: 0, rowsPerPage: 25 }))
  }

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    page: number
  ) => {
    setTableProps((s) => ({ ...s, page }))
  }

  const handleChangeRowsPerPage = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const rowsPerPage = Number(e.target.value || 25)
    setTableProps((s) => ({ ...s, rowsPerPage }))
  }

  const handleOpenDetailInvoice = (uuid: string) => {
    setModal((prevState) => ({
      ...prevState,
      isOpenDetailInvoice: true,
      uuid
    }))
  }

  const handleCloseDetailInvoice = () => {
    setModal((prevState) => ({
      ...prevState,
      isOpenDetailInvoice: false,
      uuid: ''
    }))
  }

  const handleOpenExpenseDetail = (uuid: string) => {
    setExpenseModal({ isOpen: true, uuid })
  }

  const handleCloseExpenseDetail = () => {
    setExpenseModal({ isOpen: false, uuid: '' })
  }

  const handleOpenExpenseGroup = (record: BalanceDetailItem) => {
    setExpenseGroupModal({
      isOpen: true,
      title: record.description,
      items: record.items ?? [],
      dateLabel: record.date_label
    })
  }

  const handleCloseExpenseGroup = () => {
    setExpenseGroupModal({ isOpen: false, title: '', items: [] })
  }

  const handleOpenDialog = ({
    action,
    title,
    description,
    actionLabel
  }: {
    action: () => Promise<void>
    title: string
    description: string
    actionLabel: string
  }) => {
    setDialog((s) => ({
      ...s,
      action,
      description,
      actionLabel,
      isOpenDialog: true,
      title
    }))
  }

  async function handleSubmitAlert() {
    try {
      setLoading(true)
      const response = await ReceivablesService.antecipate(selectedPaymentUuids)
      toast.success(response.message)
      setAntecipationView('history')
      setSelectedPaymentUuids([])
    } catch (err) {
      //
    } finally {
      setLoading(false)
    }
    handleCloseDialog()
  }

  useEffect(() => {
    if (!isOpen) return

    const getData = async () => {
      setTableFetchLoading(true)
      try {
        const response = await balanceDetails<BalanceDetails>()

        const data = await response?.data

        setRecords(data.records)
        setTableProps((prevState) => ({
          ...prevState,
          totalCount: Number(data.records.length)
        }))
      } catch (err) {
        setRecords([])
      } finally {
        setTableFetchLoading(false)
      }
    }

    getData()
  }, [isOpen])

  // O modal abre na hora e a tabela de saldo aparece como skeleton enquanto a
  // requisição não volta (inclusive no refetch, quando ainda há dados antigos).
  const isLoadingReceivables = records === null || tableFetchLoading
  const hasReceivables = !!records?.length

  return (
    <>
      <Modal
        aria-hidden={!isOpen}
        handleClose={handleCloseModalBalance}
        open={isOpen}
        size="large"
        width="100%"
        maxWidth={BALANCE_MODAL_MAX_WIDTH}
        height={isMobile ? 'calc(80svh - 64px)' : BALANCE_MODAL_HEIGHT}
      >
        <ModalHeader onClose={handleCloseModalBalance}>
          {isMobile && (
            <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
              <IconButton onClick={handleCloseModalBalance} size="small">
                <Icon icon="close" />
              </IconButton>
            </Box>
          )}
          <Stack
            direction={isMobile ? 'column' : 'row'}
            justifyContent="space-between"
            width="100%"
            alignItems="center"
            mr={2}
            gap={4}
            mb={isMobile ? 2 : 0}
          >
            <Stack
              direction="row"
              alignItems="center"
              gap={0.5}
              // No mobile o Stack pai centraliza (align-items: center), então
              // este filho nao esticaria: viraria shrink-to-fit e adotaria o
              // max-content das abas, estourando o modal e anulando o scroll
              // do `variant="scrollable"`. O stretch prende na largura util.
              alignSelf={isMobile ? 'stretch' : undefined}
              minWidth={isMobile ? 0 : undefined}
              // espaco pro X de fechar (absoluto no topo direito) nao cobrir a
              // seta de rolagem das abas
              pr={isMobile ? '36px' : undefined}
            >
              <Tabs
                onChange={handleChangeTab}
                tabs={buildTabs(hasActiveDisputes, tab)}
                value={tab}
                hideBottomLine
              />
            </Stack>
            {tab !== ReceivablesTabs.ANTECIPATIONS &&
              tab !== ReceivablesTabs.CONTESTS && (
                <Typography
                  variant="smBold"
                  width="fit-content"
                  whiteSpace="nowrap"
                >
                  {tab == ReceivablesTabs.RECEIVABLES ? 'Saldo:' : 'A liberar:'}{' '}
                  {formatMoney(
                    Number(
                      tab == ReceivablesTabs.RECEIVABLES
                        ? balance
                        : balanceScheduled
                    )
                  )}
                </Typography>
              )}
          </Stack>
        </ModalHeader>

        <ModalContent
          sx={{
            minHeight: 0,
            flex: 1,
            overflowY: 'auto',
            ...(!isLoadingReceivables &&
            !hasReceivables &&
            tab === ReceivablesTabs.RECEIVABLES
              ? { overflowX: 'hidden' }
              : {})
          }}
        >
          {TAB_DESCRIPTIONS[tab] && (
            <Typography
              variant="xsLight"
              component="p"
              sx={{ color: 'neutral.400', mb: 2 }}
            >
              {TAB_DESCRIPTIONS[tab]}
            </Typography>
          )}
          {tab === ReceivablesTabs.CONTESTS && (
            <DisputesList
              active={isOpen && tab === ReceivablesTabs.CONTESTS}
              initialDisputeUuid={initialDisputeUuid}
              onOpenBillingDetail={handleOpenDetailInvoice}
            />
          )}
          {tab === ReceivablesTabs.ANTECIPATIONS &&
            antecipationView === 'history' && (
              <AntecipationsList
                active={isOpen && tab === ReceivablesTabs.ANTECIPATIONS}
                onOpenBilling={handleOpenDetailInvoice}
              />
            )}
          {tab === ReceivablesTabs.ANTECIPATIONS &&
            antecipationView === 'request' && (
              <RequestAntecipation
                onSelectionChange={setSelectedPaymentUuids}
                onOpenBilling={handleOpenDetailInvoice}
              />
            )}
          {tab === ReceivablesTabs.CALENDAR_RECEIVABLES && (
            <CalendarReceivables
              balanceScheduled={balanceScheduled}
              onExportReport={() =>
                handleOpenDialog({
                  action: handleExportPendingReport,
                  description: 'Confirma exportar o relatório?',
                  title: 'Relatório',
                  actionLabel: 'Exportar'
                })
              }
              exportLoading={exportPendingLoading}
            />
          )}
          {tab === ReceivablesTabs.RECEIVABLES && (
            <>
              {isLoadingReceivables ? (
                <BalanceTableSkeleton rows={isMobile ? 4 : 8} />
              ) : !hasReceivables ? (
                <S.EmptyStateWrapper>
                  <Box maxWidth="600px" width="100%">
                    <EmptyState
                      title="Nenhum registro encontrado"
                      message="Você ainda não possui movimentações em seu saldo."
                    />
                  </Box>
                </S.EmptyStateWrapper>
              ) : (
                <Table
                  mobileCards
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                  header={[
                    { id: 'type', label: 'Tipo' },
                    { id: 'description', label: 'Descrição' },
                    { id: 'dueDate', label: 'Data de Vencimento' },
                    { id: 'paidDate', label: 'Data Pagamento' },
                    { id: 'amountPaid', label: 'Valor Pago' },
                    { id: 'fee', label: 'Tarifa bancária' },
                    { id: 'amountLiquid', label: 'Valor Líquido' },
                    { id: 'paymentType', label: 'Método de Pagamento' },
                    { id: 'actions', label: '' }
                  ]}
                  hoverInRow
                  rows={convertToRows({
                    records,
                    handleOpenDetailInvoice,
                    handleOpenExpenseDetail,
                    handleOpenExpenseGroup
                  })}
                  rowAction={(uuid) => {
                    const record = records?.find(
                      (r) => r?.referenceable_uuid === uuid
                    )
                    if (record?.type === 'Expense') {
                      if (record.grouped) {
                        handleOpenExpenseGroup(record)
                      } else {
                        handleOpenExpenseDetail(uuid)
                      }
                    } else {
                      handleOpenDetailInvoice(uuid)
                    }
                  }}
                  select={{ useSelect: false }}
                  hiddePagination
                  {...tableProps}
                />
              )}
            </>
          )}
        </ModalContent>

        <ModalFooter sx={{ flexShrink: 0 }}>
          {tab === ReceivablesTabs.RECEIVABLES && (
            <>
              {' '}
              {isLoadingReceivables && (
                <Box display="flex" gap={2} width={isMobile ? '100%' : 'auto'}>
                  <Skeleton
                    variant="rounded"
                    animation="wave"
                    height={40}
                    width={isMobile ? '100%' : 160}
                    sx={{ borderRadius: '8px' }}
                  />
                  <Skeleton
                    variant="rounded"
                    animation="wave"
                    height={40}
                    width={isMobile ? '100%' : 110}
                    sx={{ borderRadius: '8px' }}
                  />
                </Box>
              )}
              {!isLoadingReceivables && hasReceivables && !isMobile && (
                <Can permission="reports:export">
                  <Button
                    variantButton="outlined"
                    aria-label="Exportar relatório"
                    disabled={exportLoading}
                    onClick={() =>
                      handleOpenDialog({
                        action: handleExportReport,
                        description: 'Confirma exportar o relatório?',
                        title: 'Relatório',
                        actionLabel: 'Exportar'
                      })
                    }
                  >
                    Exportar Relatório
                  </Button>
                </Can>
              )}
              {!isLoadingReceivables && hasReceivables && !isMobile && (
                <Can permission="transfers:manage">
                  <Button
                    aria-label="Transferir"
                    disabled={exportLoading}
                    onClick={() =>
                      handleOpenDialog({
                        action: handleRequestWithdrawal,
                        description: 'Confirmar solicitar transferência?',
                        title: 'Saque',
                        actionLabel: 'Solicitar'
                      })
                    }
                  >
                    Transferir
                  </Button>
                </Can>
              )}
              {!isLoadingReceivables && hasReceivables && isMobile && (
                <Box display="flex" width="100%" gap={2}>
                  <Can permission="reports:export">
                    <Box flex={1}>
                      <Button
                        variantButton="outlined"
                        aria-label="Exportar relatório"
                        disabled={exportLoading}
                        style={{ width: '100%' }}
                        onClick={() =>
                          handleOpenDialog({
                            action: handleExportReport,
                            description: 'Confirma exportar o relatório?',
                            title: 'Relatório',
                            actionLabel: 'Exportar'
                          })
                        }
                      >
                        Exportar Relatório
                      </Button>
                    </Box>
                  </Can>
                  <Can permission="transfers:manage">
                    <Box flex={1}>
                      <Button
                        aria-label="Transferir"
                        disabled={exportLoading}
                        style={{ width: '100%' }}
                        onClick={() =>
                          handleOpenDialog({
                            action: handleRequestWithdrawal,
                            description: 'Confirmar solicitar transferência?',
                            title: 'Saque',
                            actionLabel: 'Solicitar'
                          })
                        }
                      >
                        Transferir
                      </Button>
                    </Box>
                  </Can>
                </Box>
              )}
            </>
          )}

          {tab === ReceivablesTabs.ANTECIPATIONS &&
            antecipationView === 'history' && (
              <Button onClick={handleRequestAntecipationClick}>
                Solicitar antecipação
              </Button>
            )}
          {tab === ReceivablesTabs.ANTECIPATIONS &&
            antecipationView === 'request' && (
              <>
                <Button
                  variantButton="outlined"
                  onClick={() => {
                    setAntecipationView('history')
                    setSelectedPaymentUuids([])
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  disabled={selectedPaymentUuids.length === 0 || loading}
                  loading={loading}
                  onClick={() =>
                    handleOpenDialog({
                      title: `Confirmar antecipação`,
                      description: `Confirma antecipar ${selectedPaymentUuids.length} recebível(is)? O valor será liberado em até 3 dias úteis. Processo sujeito a análise.`,
                      action: handleSubmitAlert,
                      actionLabel: 'Confirmar'
                    })
                  }
                >
                  Solicitar antecipação
                </Button>
              </>
            )}
        </ModalFooter>
      </Modal>

      <Dialog
        icon="description"
        title={dialog.title}
        description={dialog.description}
        setIsOpenDialog={dialog.handleClose}
        isOpenDialog={dialog.isOpenDialog}
        cancelButton
      >
        <Button
          disabled={exportLoading || loading}
          onClick={() => dialog.action()}
          fullWidth
          loading={exportLoading || loading}
        >
          {dialog.actionLabel}
        </Button>
      </Dialog>

      <DialogReport
        setDialogRedirectReport={setDialogRedirectReport}
        isOpenDialog={openDialogReport}
        loading={exportLoading}
        handleOpenReportCloseDialog={() => {
          setDialogRedirectReport(true)
          setOpenDialogReport(false)
        }}
      />
      <ModalChargeDetail
        handleCloseChargeDetail={handleCloseDetailInvoice}
        isOpenChargeDetail={modal.isOpenDetailInvoice}
        uuid={modal.uuid}
        disablePortal={false}
      />
      <ModalBalanceExpenseGroup
        isOpen={expenseGroupModal.isOpen}
        handleClose={handleCloseExpenseGroup}
        title={expenseGroupModal.title}
        items={expenseGroupModal.items}
        dateLabel={expenseGroupModal.dateLabel}
        // NÃO fecha o grupo: o detalhe individual empilha em cima. Fechando, o merchant perdia
        // a lista e tinha de reabrir o grupo para ver o próximo item.
        onOpenExpenseDetail={handleOpenExpenseDetail}
      />
      <ModalBalanceExpenseDetail
        isOpen={expenseModal.isOpen}
        handleClose={handleCloseExpenseDetail}
        uuid={expenseModal.uuid}
        hideDebitedDate
        onOpenBillingDetail={(billingUuid) => {
          handleOpenDetailInvoice(billingUuid)
        }}
        onOpenNegativationDetail={(negUuid) => {
          setNegativationDetailUuid(negUuid)
        }}
        onOpenNfseDetail={(nfseUuid) => {
          setNfseDetailUuid(nfseUuid)
        }}
        onOpenTransferDetail={(uuid) => {
          setTransferDetailUuid(uuid)
        }}
      />
      <NegativationProvider>
        {negativationDetailUuid && (
          <NegativationDetailOpener
            uuid={negativationDetailUuid}
            onDone={() => setNegativationDetailUuid('')}
          />
        )}
        <NegativationDetail />
      </NegativationProvider>
      <ChargeContextProvider>
        {nfseDetailUuid && (
          <NfseDetailOpener
            uuid={nfseDetailUuid}
            onDone={() => setNfseDetailUuid('')}
          />
        )}
        {transferDetailUuid && (
          <TransferDetailOpener
            uuid={transferDetailUuid}
            onDone={() => setTransferDetailUuid('')}
          />
        )}
        <ModalInvoiceDetails />
        <ModalTransfers onOpenExpenseDetail={handleOpenExpenseDetail} />
      </ChargeContextProvider>
      {dialogRedirectReport && (
        <ModalReport
          handleCloseReportModal={() => setDialogRedirectReport(false)}
          isOpenModalReport={dialogRedirectReport}
        />
      )}
    </>
  )
}
